/* Picture controls: flip, focus, zoom, colour, exposure.
 *
 * rpicam-vid takes these only when it starts, so every change the node applies
 * restarts its video for a few seconds. The panel therefore edits a draft and
 * sends it on Apply, never on a slider tick. And since nothing here is instant
 * -- the camera picks a change up at its next 10 s check and says what it is
 * running with at its next report -- the panel shows the question beside the
 * answer until they agree.
 */
import { useEffect, useState } from 'react'
import {
  ApiError, fmtTime, setCameraControls,
  type CameraControls, type CameraControlsView,
} from '../api'

const errText = (e: unknown, fallback: string) =>
  e instanceof ApiError ? e.detail ?? e.code : fallback

const AWB: { id: NonNullable<CameraControls['awb']>; label: string }[] = [
  { id: 'auto', label: 'Auto' },
  { id: 'daylight', label: 'Daylight' },
  { id: 'cloudy', label: 'Cloudy' },
  { id: 'indoor', label: 'Indoor' },
  { id: 'fluorescent', label: 'Fluorescent' },
  { id: 'incandescent', label: 'Incandescent' },
  { id: 'tungsten', label: 'Tungsten' },
  { id: 'custom', label: 'Custom gains' },
]

/** A NoIR module under daylight comes out pink; this is a sensible start. */
const DEFAULT_GAINS: [number, number] = [1.5, 1.8]

const same = (a: unknown, b: unknown) =>
  JSON.stringify(a, Object.keys(a ?? {}).sort()) ===
  JSON.stringify(b, Object.keys(b ?? {}).sort())

/** Whether the node runs what was asked: every asked key is either running
 *  or refused with a reason, and nothing else is running. */
export function controlsSettled(v: CameraControlsView | undefined): boolean {
  if (!v || !v.supported || !v.effective) return true
  const eff = v.effective
  const asked = v.asked
  for (const k of Object.keys(asked) as (keyof CameraControls)[]) {
    if (k in v.refused) continue
    if (JSON.stringify(asked[k]) !== JSON.stringify(eff[k])) return false
  }
  return Object.keys(eff).every((k) => k in asked)
}

function lensLabel(d: number): string {
  if (d <= 0) return '∞ (infinity)'
  const cm = 100 / d
  return cm >= 100 ? `≈ ${(cm / 100).toFixed(1)} m` : `≈ ${Math.round(cm)} cm`
}

/** A slider whose absent value is the camera's default, shown as such. */
function Slider({ label, value, def, min, max, step, fmt, disabled, onChange }: {
  label: string
  value: number | undefined
  def: number
  min: number
  max: number
  step: number
  fmt?: (v: number) => string
  disabled?: boolean
  onChange: (v: number | undefined) => void
}) {
  const v = value ?? def
  return (
    <label className="stack" style={{ gap: 2 }}>
      <span className="row" style={{ gap: 8, justifyContent: 'space-between' }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
        <span className="num faint" style={{ fontSize: 12 }}>
          {fmt ? fmt(v) : v.toFixed(step < 0.1 ? 2 : 1)}
          {value === undefined ? ' · default' : (
            <button type="button" className="btn ghost sm" disabled={disabled}
                    style={{ marginLeft: 6, minHeight: 0, padding: '0 6px' }}
                    onClick={(e) => { e.preventDefault(); onChange(undefined) }}>
              reset
            </button>
          )}
        </span>
      </span>
      <input type="range" min={min} max={max} step={step} value={v}
             disabled={disabled}
             onChange={(e) => {
               const n = Number(e.target.value)
               onChange(Math.abs(n - def) < step / 2 ? undefined : n)
             }} />
    </label>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      <legend style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em',
                       color: 'var(--muted)', marginBottom: 6 }}>{title}</legend>
      <div className="stack" style={{ gap: 8 }}>{children}</div>
    </fieldset>
  )
}

export function CameraControlsPanel({ computeId, view, canWrite, onChanged }: {
  computeId: string
  view: CameraControlsView | undefined
  canWrite: boolean
  onChanged: () => void
}) {
  const asked = view?.asked ?? {}
  const [draft, setDraft] = useState<CameraControls>(asked)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const dirty = !same(draft, asked)

  // Follow what is stored while nothing is being edited, so a change made in
  // another tab, or by Apply, shows here.
  const askedKey = JSON.stringify(asked)
  useEffect(() => {
    if (!dirty) setDraft(JSON.parse(askedKey) as CameraControls)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askedKey])

  if (!view) return null
  const off = !canWrite || !view.supported || busy

  const set = <K extends keyof CameraControls>(k: K, v: CameraControls[K] | undefined) =>
    setDraft((d) => {
      const n = { ...d }
      if (v === undefined) delete n[k]
      else n[k] = v
      return n
    })

  const send = async (controls: CameraControls) => {
    setBusy(true)
    setErr(null)
    try {
      await setCameraControls(computeId, controls)
      setDraft(controls)
      onChanged()
    } catch (e) {
      setErr(errText(e, 'Could not save the controls.'))
    } finally {
      setBusy(false)
    }
  }

  const focus = draft.lens_position !== undefined ? 'manual' : draft.af_mode
  const zoom = draft.zoom ?? 1
  const center = draft.zoom_center ?? [0.5, 0.5]
  const settled = controlsSettled(view)
  const refused = Object.entries(view.refused)

  return (
    <div className="stack" style={{ gap: 14 }}>
      {!view.supported && (
        <div className="notice">
          This node's software predates camera controls, so it would ignore
          them. Update it to <span className="mono">3b5960f</span> or later
          (flip needs the build after that).
        </div>
      )}
      {view.source === 'local' && (
        <div className="notice">
          Someone at the node has set the controls by hand
          (<span className="mono">phoniq camera-controls</span>), and that wins
          until it is cleared or the node restarts. What you apply here is kept
          and takes over then.
        </div>
      )}

      <div style={{ display: 'grid', gap: 18,
                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
        <Section title="Orientation">
          <label className="row" style={{ gap: 8 }}>
            <input type="checkbox" checked={!!draft.hflip} disabled={off}
                   onChange={(e) => set('hflip', e.target.checked || undefined)} />
            <span>Flip horizontally</span>
          </label>
          <label className="row" style={{ gap: 8 }}>
            <input type="checkbox" checked={!!draft.vflip} disabled={off}
                   onChange={(e) => set('vflip', e.target.checked || undefined)} />
            <span>Flip vertically</span>
          </label>
          <div>
            <button type="button" className="btn ghost sm" disabled={off}
                    onClick={() => {
                      const on = !(draft.hflip && draft.vflip)
                      set('hflip', on || undefined)
                      set('vflip', on || undefined)
                    }}>
              Rotate 180°
            </button>
          </div>
        </Section>

        <Section title="Focus">
          <div className="seg sm" role="tablist" aria-label="Focus">
            {([['continuous', 'Continuous'], ['auto', 'Once'], ['manual', 'Manual']] as const)
              .map(([id, label]) => (
                <button key={id} type="button" role="tab" disabled={off}
                        aria-selected={(focus ?? 'continuous') === id}
                        onClick={() => setDraft((d) => {
                          const n = { ...d }
                          if (id === 'manual') {
                            n.af_mode = 'manual'
                            n.lens_position = d.lens_position ?? 1
                          } else {
                            delete n.lens_position
                            if (id === 'continuous') delete n.af_mode
                            else n.af_mode = id
                          }
                          return n
                        })}>
                  {label}
                </button>
              ))}
          </div>
          {focus === 'manual' ? (
            <Slider label="Distance" value={draft.lens_position} def={1}
                    min={0} max={10} step={0.1} fmt={lensLabel} disabled={off}
                    onChange={(v) => set('lens_position', v ?? 1)} />
          ) : (
            <p className="faint" style={{ fontSize: 12, margin: 0 }}>
              {focus === 'auto'
                ? 'Focuses once each time the video starts, then holds.'
                : 'Keeps refocusing as the scene changes.'}
            </p>
          )}
          {focus !== 'manual' && (
            <label className="stack" style={{ gap: 2 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Search range</span>
              <select value={draft.af_range ?? 'normal'} disabled={off}
                      onChange={(e) => set('af_range', e.target.value === 'normal'
                        ? undefined : e.target.value as CameraControls['af_range'])}>
                <option value="normal">Normal</option>
                <option value="macro">Close up</option>
                <option value="full">Everything</option>
              </select>
            </label>
          )}
        </Section>

        <Section title="Zoom">
          <Slider label="Zoom" value={draft.zoom} def={1} min={1} max={8} step={0.1}
                  fmt={(v) => `${v.toFixed(1)}×`} disabled={off}
                  onChange={(v) => setDraft((d) => {
                    const n = { ...d }
                    if (v === undefined) { delete n.zoom; delete n.zoom_center }
                    else n.zoom = v
                    return n
                  })} />
          {zoom > 1 && (
            <>
              <Slider label="Left ↔ right" value={draft.zoom_center?.[0]} def={0.5}
                      min={0} max={1} step={0.01} disabled={off}
                      fmt={(v) => `${Math.round(v * 100)} %`}
                      onChange={(v) => set('zoom_center', [v ?? 0.5, center[1]])} />
              <Slider label="Up ↕ down" value={draft.zoom_center?.[1]} def={0.5}
                      min={0} max={1} step={0.01} disabled={off}
                      fmt={(v) => `${Math.round(v * 100)} %`}
                      onChange={(v) => set('zoom_center', [center[0], v ?? 0.5])} />
            </>
          )}
        </Section>

        <Section title="Colour">
          <label className="stack" style={{ gap: 2 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>White balance</span>
            <select value={draft.awb ?? 'auto'} disabled={off}
                    onChange={(e) => setDraft((d) => {
                      const n = { ...d }
                      const v = e.target.value as NonNullable<CameraControls['awb']>
                      if (v === 'auto') delete n.awb
                      else n.awb = v
                      if (v === 'custom') n.awb_gains = d.awb_gains ?? DEFAULT_GAINS
                      else delete n.awb_gains
                      return n
                    })}>
              {AWB.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
          </label>
          {draft.awb === 'custom' && (
            <>
              <Slider label="Red gain" value={draft.awb_gains?.[0]} def={DEFAULT_GAINS[0]}
                      min={0.1} max={8} step={0.05} disabled={off}
                      onChange={(v) => set('awb_gains',
                        [v ?? DEFAULT_GAINS[0], draft.awb_gains?.[1] ?? DEFAULT_GAINS[1]])} />
              <Slider label="Blue gain" value={draft.awb_gains?.[1]} def={DEFAULT_GAINS[1]}
                      min={0.1} max={8} step={0.05} disabled={off}
                      onChange={(v) => set('awb_gains',
                        [draft.awb_gains?.[0] ?? DEFAULT_GAINS[0], v ?? DEFAULT_GAINS[1]])} />
            </>
          )}
          <label className="row" style={{ gap: 8 }}>
            <input type="checkbox" checked={draft.saturation === 0} disabled={off}
                   onChange={(e) => set('saturation', e.target.checked ? 0 : undefined)} />
            <span>Black and white</span>
          </label>
          {draft.saturation !== 0 && (
            <Slider label="Saturation" value={draft.saturation} def={1}
                    min={0} max={4} step={0.05} disabled={off}
                    onChange={(v) => set('saturation', v)} />
          )}
        </Section>

        <Section title="Picture">
          <Slider label="Brightness" value={draft.brightness} def={0}
                  min={-1} max={1} step={0.05} disabled={off}
                  onChange={(v) => set('brightness', v)} />
          <Slider label="Contrast" value={draft.contrast} def={1}
                  min={0} max={4} step={0.05} disabled={off}
                  onChange={(v) => set('contrast', v)} />
          <Slider label="Sharpness" value={draft.sharpness} def={1}
                  min={0} max={4} step={0.05} disabled={off}
                  onChange={(v) => set('sharpness', v)} />
          <Slider label="Exposure" value={draft.ev} def={0}
                  min={-8} max={8} step={0.5} disabled={off}
                  fmt={(v) => `${v > 0 ? '+' : ''}${v.toFixed(1)} EV`}
                  onChange={(v) => set('ev', v)} />
        </Section>
      </div>

      {canWrite && (
        <div className="row" style={{ gap: 8 }}>
          <button className="btn sm" disabled={off || !dirty}
                  onClick={() => void send(draft)}>
            {busy ? 'Saving…' : 'Apply'}
          </button>
          <button className="btn sm ghost" disabled={off || !dirty}
                  onClick={() => setDraft(asked)}>Revert</button>
          <button className="btn sm ghost"
                  disabled={off || (Object.keys(asked).length === 0 && !dirty)}
                  onClick={() => void send({})}>Reset to defaults</button>
          {dirty && (
            <span className="faint" style={{ fontSize: 12 }}>
              Unsaved. Applying restarts the video for a few seconds.
            </span>
          )}
        </div>
      )}
      {err && <div className="notice bad">{err}</div>}

      {view.supported && !dirty && (
        settled ? (
          view.asked_at && (
            <p className="faint" style={{ fontSize: 12, margin: 0 }}>
              Running on the camera. Set {fmtTime(view.asked_at)}
              {view.asked_by ? ` by ${view.asked_by}` : ''}.
            </p>
          )
        ) : (
          <div className="notice">
            Waiting for the camera to report that it runs these. It picks a
            change up within about 10 seconds; the report follows with the
            node's next check-in.
          </div>
        )
      )}
      {refused.length > 0 && (
        <div className="notice bad">
          The node would not apply:
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {refused.map(([k, why]) => (
              <li key={k}><span className="mono">{k}</span>: {why}</li>
            ))}
          </ul>
          {view.refused.hflip || view.refused.vflip
            ? 'Flip needs the node software after 3b5960f.' : null}
        </div>
      )}
    </div>
  )
}
